import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as ExportIcon } from '@ubeya/shared-web/assets/file-excel.svg';
import { CircleIcon, Modal, Tooltip } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';

import useBranches from '@ubeya/shared/hooks/account/useBranches';
import usePayrollFields from '@ubeya/shared/hooks/account/usePayrollFields';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import { SORT_BY_DATE } from '@ubeya/shared/utils/sorting';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useSuppliers from '@ubeya/shared/hooks/account/useSuppliers';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import { filterPayrollFieldsByDate, generateProjectsClientCSV } from 'shared/utils/payroll';
import { EmptyState } from '../EmptyState';
import BaseInvoiceTableReadOnly from './InvoiceTableReadOnly';

const Container = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  flex-direction: column;
`;

const Wrapper = styled.div`
  overflow-y: auto;
  height: 100%;
  padding: 20px;
`;

const Title = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-left: 20px;
  padding: 10px 20px 0 0;
`;

const ProjectSection = styled.div`
  margin: 20px 0;
`;
const Name = styled.div`
  margin-bottom: 10px;
`;

const InvoiceTableReadOnly = styled(BaseInvoiceTableReadOnly)`
  padding: 0;
  > div {
    overflow-x: scroll;
  }
`;

const DetailedInvoicesModal = ({
  employeesMap,
  title,
  groupedData,
  onClose,
  isClient = false,
  timeRange,
  countryCode,
  filePrefix
}) => {
  const { accountId, account } = useAccount();
  const { mappedBranches } = useBranches();
  const { mappedPositions } = usePositions();
  const { mappedLocations } = useLocations();
  const { mappedSupplierPositions } = useSuppliers();
  const { fields: allFields } = usePayrollFields();
  const { t, dateFormat, timeFormat } = useContext(ConfigContext);

  const dateSorter = SORT_BY_DATE('date');

  const payrollFields = filterPayrollFieldsByDate(allFields, timeRange.start);
  const fields = useMemo(() => (payrollFields || []).filter((field) => !!field.isClient), [payrollFields]);

  const groupedDataSorted = useMemo(() => groupedData.sort(dateSorter), [dateSorter, groupedData]);

  const csvParams = useMemo(
    () => ({
      accountId,
      groupedData,
      mappedBranches,
      employeesMap,
      filePrefix: filePrefix || account?.name,
      t,
      timeFormat,
      dateFormat,
      mappedPositions,
      mappedSupplierPositions,
      fields,
      countryCode,
      mappedLocations
    }),
    [
      accountId,
      groupedData,
      mappedBranches,
      employeesMap,
      filePrefix,
      account?.name,
      t,
      timeFormat,
      dateFormat,
      mappedPositions,
      mappedSupplierPositions,
      fields,
      countryCode,
      mappedLocations
    ]
  );

  const exportDetailedWithSummaryCSV = useCallback(() => {
    generateProjectsClientCSV(csvParams);
  }, [csvParams]);

  return (
    <Modal
      onClose={onClose}
      showConfirmButton={false}
      isPaddingContent={false}
      title={title}
      closeOnClickOutside={false}>
      <Container>
        <Title>
          <Tooltip body={t('exportFullReport')}>
            <CircleIcon icon={ExportIcon} onClick={exportDetailedWithSummaryCSV} />
          </Tooltip>
        </Title>
        <Wrapper>
          {(groupedDataSorted || []).length === 0 ? (
            <EmptyState text={t('noData')} />
          ) : (
            (groupedDataSorted || []).map((data, index) => (
              <ProjectSection key={index}>
                <Name>{data?.groupValue}</Name>

                <InvoiceTableReadOnly
                  data={data.subRows}
                  isClient={isClient}
                  timeRange={timeRange}
                  storageName={`projects-invoice-${accountId}-${data.date}`}
                />
              </ProjectSection>
            ))
          )}
        </Wrapper>
      </Container>
    </Modal>
  );
};

export default DetailedInvoicesModal;
